import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { money, PLACEHOLDER } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import WithdrawDialog from "@/components/WithdrawDialog";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Trash2, Wallet } from "lucide-react";

export default function AdminPanel({ onChange }) {
  const { t } = useLang();
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [kyc, setKyc] = useState([]);
  const [commission, setCommission] = useState(null);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);

  const load = useCallback(async () => {
    const [p, a, k, c, wd] = await Promise.all([api.get("/admin/studios/pending"), api.get("/admin/studios"), api.get("/admin/kyc"), api.get("/admin/commission"), api.get("/admin/withdrawals")]);
    setPending(p.data.studios);
    setAll(a.data.studios);
    setKyc(k.data.users);
    setCommission(c.data);
    setWithdrawals(wd.data.withdrawals);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, approve) => {
    await api.post(`/admin/studios/${id}/verify`, null, { params: { approve } });
    toast.success(approve ? t("approve") : t("reject"));
    load();
    onChange?.();
  };

  const del = async (id) => {
    await api.delete(`/studios/${id}`);
    toast.success(t("saved"));
    load();
    onChange?.();
  };

  const verifyKyc = async (uid, approve) => {
    await api.post(`/admin/kyc/${uid}/verify`, null, { params: { approve } });
    toast.success(approve ? t("verify") : t("reject"));
    load();
  };

  const setWdStatus = async (id, status) => {
    await api.post(`/admin/withdrawals/${id}/status`, null, { params: { status } });
    toast.success(status === "completed" ? t("mark_paid") : t("reject"));
    load();
    onChange?.();
  };

  return (
    <div className="mt-8 p-5 rounded-2xl border border-primary/30 bg-primary/5">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <h2 className="font-display text-xl font-bold">{t("admin_panel")}</h2>
      </div>
      {commission && (
        <div data-testid="commission-stat" className="mb-4 p-4 rounded-xl border border-accent/40 bg-accent/10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">{t("commission_earned")}</span>
            <span className="font-display font-bold text-accent">{money(commission.total_commission)} <span className="text-xs text-muted-foreground">({commission.count})</span></span>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{t("commission_available")}</p>
              <p data-testid="commission-available" className="font-display text-2xl font-extrabold text-accent">{money(commission.available || 0)}</p>
            </div>
            <Button data-testid="admin-withdraw-btn" onClick={() => setShowWithdraw(true)} disabled={(commission.available || 0) <= 0} size="sm" className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
              <Wallet className="w-4 h-4 mr-1" /> {t("withdraw")}
            </Button>
          </div>
          {commission.withdrawals && commission.withdrawals.length > 0 && (
            <div className="mt-3 pt-3 border-t border-accent/20 space-y-1" data-testid="commission-withdrawals">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{t("commission_withdrawals")}</p>
              {commission.withdrawals.map((w) => (
                <div key={w.id} className="flex justify-between text-sm">
                  <span>{t(w.method === "paypal" ? "paypal" : "bank")}</span>
                  <span className="text-muted-foreground">{money(w.amount)} · {w.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {withdrawals.length > 0 && (
        <div className="mb-6" data-testid="admin-withdrawals">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("manage_withdrawals")} ({withdrawals.length})</p>
          <div className="space-y-2">
            {withdrawals.map((w) => (
              <div key={w.id} data-testid={`wd-${w.id}`} className="p-3 rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{money(w.amount)} · {t(w.method === "paypal" ? "paypal" : "bank")} <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground ml-1">{w.kind === "commission" ? t("commission_label") : t("wallet_label")}</span></p>
                    <p className="text-xs text-muted-foreground truncate">{w.user_name || w.user_email} · {w.destination}{w.swift ? " · " + w.swift : ""}</p>
                  </div>
                  <span className={`text-[11px] font-semibold shrink-0 ${w.status === "completed" ? "text-emerald-400" : w.status === "rejected" ? "text-red-400" : "text-amber-400"}`}>{t(`wd_${w.status}`)}</span>
                </div>
                {w.status === "requested" && (
                  <div className="flex gap-2 mt-2">
                    <Button data-testid={`wd-complete-${w.id}`} onClick={() => setWdStatus(w.id, "completed")} size="sm" className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white h-8"><Check className="w-4 h-4 mr-1" /> {t("mark_paid")}</Button>
                    <Button data-testid={`wd-reject-${w.id}`} onClick={() => setWdStatus(w.id, "rejected")} size="sm" variant="ghost" className="rounded-lg text-red-400 hover:bg-red-500/10 h-8"><X className="w-4 h-4 mr-1" /> {t("reject")}</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("pending_verification")} ({pending.length})</p>
      {pending.length === 0 ? (
        <p data-testid="no-pending" className="text-sm text-muted-foreground">{t("no_pending")}</p>
      ) : (
        <div className="space-y-3">
          {pending.map((s) => (
            <div key={s.id} data-testid={`pending-studio-${s.id}`} className="p-3 rounded-xl border border-border bg-card">
              <div className="flex gap-2 overflow-x-auto mb-2">
                {(s.photos && s.photos.length ? s.photos : [PLACEHOLDER]).map((p, i) => {
                  const src = /^https?:\/\//.test(p) ? p : PLACEHOLDER;
                  return (
                    <a key={i} href={src} target="_blank" rel="noreferrer" data-testid={`pending-photo-${s.id}-${i}`} className="shrink-0">
                      <img src={src} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-36 h-24 rounded-lg object-cover hover:ring-2 hover:ring-primary transition-all" />
                    </a>
                  );
                })}
              </div>
              <p className="font-semibold">{s.name} <span className="text-xs text-muted-foreground">· {s.type === "recording" ? t("recording") : t("rehearsal")}</span></p>
              <p className="text-xs text-muted-foreground">{s.owner_name} · {s.address ? s.address + ", " : ""}{s.city} · {money(s.price_per_hour, s.currency)}{t("per_hour")}</p>
              {s.description && <p className="text-sm text-muted-foreground mt-1">{s.description}</p>}
              {s.amenities && s.amenities.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{s.amenities.map((a) => <span key={a} className="text-[11px] px-2 py-1 rounded-md bg-secondary text-muted-foreground">{a}</span>)}</div>}
              {s.engineer_available && <p className="text-xs text-emerald-400 mt-1">{t("with_engineer")}: {money(s.engineer_price_per_hour, s.currency)}{t("per_hour")}{s.engineer_bio ? " · " + s.engineer_bio : ""}</p>}
              <div className="flex gap-2 mt-3">
                <Button data-testid={`approve-${s.id}`} onClick={() => act(s.id, true)} size="sm" className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white h-8"><Check className="w-4 h-4 mr-1" /> {t("approve")}</Button>
                <Button data-testid={`reject-${s.id}`} onClick={() => act(s.id, false)} size="sm" variant="ghost" className="rounded-lg text-red-400 hover:bg-red-500/10 h-8"><X className="w-4 h-4 mr-1" /> {t("reject")}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-6 mb-3">{t("kyc_admin")} ({kyc.length})</p>
      <div className="space-y-2 mb-2" data-testid="admin-kyc">
        {kyc.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : kyc.map((u) => (
          <div key={u.user_id} data-testid={`kyc-${u.user_id}`} className="p-3 rounded-xl border border-border bg-card">
            <div className="flex gap-3">
              {u.kyc_document && <a href={u.kyc_document} target="_blank" rel="noreferrer"><img src={u.kyc_document} alt="ID" className="w-20 h-14 rounded object-cover border border-border" /></a>}
              <div className="flex-1 min-w-0 text-sm">
                <p className="font-semibold">{u.real_name || u.artist_name || u.email}</p>
                {u.artist_name && <p className="text-xs text-muted-foreground">{t("artist_name")}: {u.artist_name}</p>}
                <p className="text-xs text-muted-foreground">{u.email}{u.phone ? " · " + u.phone : ""}</p>
                {u.address && <p className="text-xs text-muted-foreground">{t("address")}: {u.address}</p>}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button data-testid={`kyc-approve-${u.user_id}`} onClick={() => verifyKyc(u.user_id, true)} size="sm" className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white h-8"><Check className="w-4 h-4 mr-1" /> {t("verify")}</Button>
              <Button data-testid={`kyc-reject-${u.user_id}`} onClick={() => verifyKyc(u.user_id, false)} size="sm" variant="ghost" className="rounded-lg text-red-400 hover:bg-red-500/10 h-8"><X className="w-4 h-4 mr-1" /> {t("reject")}</Button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-6 mb-3">{t("all_studios")} ({all.length})</p>
      <div className="space-y-2">
        {all.map((s) => (
          <div key={s.id} data-testid={`admin-studio-${s.id}`} className="flex items-center gap-3 p-2.5 rounded-xl border border-border bg-card">
            <span className="flex-1 min-w-0 truncate text-sm">{s.name}<span className="text-xs text-muted-foreground"> · {s.city} · {s.verified ? t("status_approved") : t("status_pending")}</span></span>
            <Button data-testid={`admin-delete-${s.id}`} onClick={() => del(s.id)} variant="ghost" size="icon" className="text-red-400 hover:bg-red-500/10 h-8 w-8">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
      <WithdrawDialog open={showWithdraw} onClose={() => setShowWithdraw(false)} max={commission?.available || 0} endpoint="/admin/commission/withdraw" onDone={load} />
    </div>
  );
}
